/**
 * 63.1n with 67.75, the motor pool is the stock (the owner, Oct 20: "Yes pool should be that stock, we should integrate those
 * two.  Lost vehicles can be replaced, not sure what trigger should be.  Empty pool means no more accessible vehicles yes";
 * his picks on the note: "Agree D sounds good fo rreplacement trigger.  For where replacements go, for now long drive, and we
 * do have dropship vehicle delivery capabilities so we can run with that.  I think in general vehicles should be delivered
 * unpacked when flown via dropship."), with GameConfig.motorPool and the vehicle stock:
 * - each side's spares are frames in its motor pool behind its base (core/motorPool.ts: its site, its plan; the frames are the
 *   stock's counts, `vehicleStocks`, stacked in the fill's order — one source, nothing kept twice);
 * - a replacement is an **unpack**: a frame of its kind leaves the pool for the unpack pad inside the gate and the vehicle
 *   stands there MOTOR_POOL_STOCK.unpackSeconds later (the sim's step; the stacker arm and the corner robots draw it), one at
 *   a time on the pad; it drives out of the gate (the long drive to the front);
 * - the trigger is the side's **standing order** (the owner's D): `auto` (the default, nothing written) unpacks a lost
 *   vehicle's replacement when it is due (core/vehicleStock.ts' queue); `order` holds the frames, and the commander unpacks
 *   a kind by hand (the `motorPool` input). The bot commander stays on auto;
 * - a called APC drop takes its vehicle from the pool too (core/dropship.ts: its spare spent at the call is a frame — flown,
 *   delivered unpacked — refused as the pool refuses an unpack of its kind, the pad aside);
 * - **an empty pool** — no frame of a kind — means no more of that kind.
 * Pure and deterministic; off (no pools), nothing here runs.
 */
import { MORTAR } from '../constants/action';
import type { GameState, StructureState, Vec3, VehicleKind } from '../types';
import { MOTOR_POOL_STOCK, START_UNWRAPPED, START_UNPACK_ORDER, READY_LINE } from '../constants/action';
import { VEHICLE_KINDS } from '../constants/body';
import { PHYS_DT } from '../constants';
import { MOTOR_POOL, motorPoolSite, planCentre, motorPoolLoads, fillMotorPool, skirtOf, poolHardstand } from './motorPool';
import { footprintGround, gateRamps, terraceEdit } from './terraces';
import { TERRACE } from './terraceGround';
import { applyTerrainEdit } from './terrainEdits';
import { footingOf } from './stands';
import { structureTeam } from './body';
import type { Terrace } from '../types/ground';
import { structureHealth } from './body';
import { unpackRefusal, poolOf } from './poolRefusal';

export { poolOf, unpackRefusal, PAD_BUSY } from './poolRefusal';
import { initialSeat } from './seats';
import { structureFootprint } from './buildingScale';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecCross, vecDot, vecNormalize, vecScale, uprightFrame, surfaceTangentFrame } from '../math';
import { datan2 } from './dmath';
import { seatBuild } from './buildSeat';
import { withStands } from './stands';


/** The setup's pools: with the switch and the stock, each stocked side's pool where its site stands now (behind its base). */
/**
 * 69.14 (the owner: "footing can disappear"; DECISIONS 235): each side that will stock a pool gets a terrace under its compound's
 * site first — a flat pad the plinth's size at the median of the ground under it, with the batter band round it — laid through
 * the terrain log before the base's decks are seated (match/setup.ts), so they are seated on the ground it leaves. Its plinth
 * then sits on the level with a step's skirt: it was seated on the highest ground under its 36 × 36 people and its skirt
 * ramped over the HQ's and the pad's decks, burying them (up to 4.6 and 17 people). With the fine ground only (the pool is
 * person scale); a side that will stock one: the switch, the stock and a vehicle of its own (withVehicleStocks' rule).
 */
export function layPoolTerraces(state: GameState): GameState {
  if (!state.config.motorPool) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, foot = footingOf({ type: 'motorPool' }, state.config);
  if (!(ps.fineAmplitude > 0) || !foot) return state;
  const teams = poolTeams(state);
  let planets = state.planets, edits = state.terrainEdits ?? [], laid = false, nextId = 1;
  const sites: NonNullable<GameState['motorPools']> = {};
  for (const p of planets) for (const t of p.terraces ?? []) nextId = Math.max(nextId, t.id + 1);
  for (const team of teams) {
    const site = motorPoolSite({ ...state, planets }, team), planet = site ? planets[site.planetIndex] : undefined;
    if (!site || !planet || planet.type !== 'planet' || !planet.alive) continue;
    // (69.22: the hardstand — the compound and the ready line before its gate, level together; its centre forward of the compound's)
    const hs = poolHardstand(foot), tz = vecNormalize(site.front);
    const up = vecNormalize(vecAdd(vecNormalize(site.dir), vecScale(tz, (hs.offsetPeople * person) / planet.radius)));
    const across = vecNormalize(vecCross(tz, up));
    const halfX = hs.halfXPeople * person, halfZ = hs.halfZPeople * person;
    const { level } = footprintGround(planet, up, tz, across, halfX, halfZ, ps);
    // (69.22: its coarse apron leaves the base's ground alone — every deck on the planet with its skirt, as it will be seated)
    const apronKeep = state.structures.filter(s => s.planetIndex === site.planetIndex && s.health > 0 && s.type !== 'motorPool')
      .map(s => ({ s, f: footingOf(s, state.config) })).filter(x => x.f)
      .map(({ s, f }) => ({ dir: vecNormalize(s.surfaceDir), reach: Math.sqrt(f!.halfXPeople * f!.halfXPeople + f!.halfZPeople * f!.halfZPeople) * person + skirtOf({ ...state, planets }, s) }));
    const t0: Terrace = { id: nextId++, dir: up, facing: tz, halfX, halfZ, band: TERRACE.bandPeople * person, level, ...(apronKeep.length ? { apronKeep } : {}) };
    // (69.42, lane H's finding: the compound's gates' ramps — a supply truck out by the back gate onto a cut face stopped there)
    const ramps = gateRamps(planet, t0, { type: 'motorPool', surfaceDir: site.dir, yaw: poolYaw(site) }, state.config, ps), t: Terrace = ramps.length ? { ...t0, ramps } : t0;
    const e = terraceEdit(site.planetIndex, planet, t);
    planets = planets.map((p, i) => (i === site.planetIndex ? applyTerrainEdit(p, e) : p));
    edits = [...edits, e]; laid = true;
    sites[team] = site;
  }
  // (69.22: the sites kept — withMotorPools stands each compound on its own terrace; worked out again there, on the ground the
  // terraces and the seated decks left, a site moved with every skirt it reads)
  return laid ? { ...state, planets, terrainEdits: edits, motorPools: { ...(state.motorPools ?? {}), ...sites } } : state;
}

/** The compound's yaw at its site: its gate (−z, toward `front`) as the upright frame turns it (core/hits.ts structureFrame). */
function poolYaw(site: { dir: Vec3; front: Vec3 }): number {
  const f = uprightFrame(vecNormalize(site.dir));
  return datan2(-vecDot(site.front, f.tx), -vecDot(site.front, f.tz));
}

/** 69.14: the sides that stand a pool — every side with people (a side with no vehicles stocked stands an empty compound: its supplies' containers in it). */
function poolTeams(state: Pick<GameState, 'players'>): number[] {
  return [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
}

export function withMotorPools(state: GameState): GameState {
  if (!state.config.motorPool) return state;
  const pools: NonNullable<GameState['motorPools']> = {};
  // 63.16 (7) (the owner: "no part of motor pool is standable or has collisions"): the compound stands as a structure of its
  // own — its Hesco square and gates (core/modules.ts BASE_TEMPLATES.motorPool, the layout the look draws) as a modular
  // building's: the hit shape, the walk's discs, the plinth stand, the route graph through its gates. Its gates open
  // (`shut: []`: the unpacked vehicle drives out by the front one, the lighters' frames come in by the back); the side's
  // lead unit its owner; its yaw the look's (the gate, −z, toward `front`: the upright frame's, as core/hits.ts
  // structureFrame turns it). Seated and stood here: the setup's seat and stands pass ran before the pool was sited.
  const extra: StructureState[] = [];
  // (69.14's fix-forward: ids past the highest — the base planner drops what stands in a base's way before this, so the count
  // fell short and a pool took a standing structure's id: the owner's side's pool shared a drop container's, its walls never drawn)
  const firstId = state.structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
  for (const team of poolTeams(state)) {   // (69.14: every side — it was the stocked ones; a pool is drawn only where it stands, so none is drawn alone)
    const site = state.motorPools?.[team] ?? motorPoolSite(state, team);   // (69.22: the site its terrace was laid at)
    if (!site) continue;
    pools[team] = { planetIndex: site.planetIndex, dir: site.dir, front: site.front };
    const lead = state.players.filter(p => p.teamIndex === team).sort((a, b) => a.id - b.id)[0];
    extra.push(seatBuild(state, { id: firstId + extra.length, type: 'motorPool', planetIndex: site.planetIndex, surfaceDir: site.dir, yaw: poolYaw(site), health: structureHealth('motorPool'), ownerId: lead?.id ?? -1, shut: [] }));
  }
  if (!Object.keys(pools).length) return state;
  return syncPoolStacks(withStands({ ...state, motorPools: pools, structures: [...state.structures, ...extra] }));
}

/** A pool's stacks as its look stands them: each slot's tiers (MOTOR_POOL.slots' order) from its side's loads (fillMotorPool). */
function stacksOf(state: GameState, team: number, tick: number): number[] {
  const tiers = new Array<number>(MOTOR_POOL.slots.length).fill(0);
  for (const st of fillMotorPool(MOTOR_POOL, motorPoolLoads({ ...state, tick }, team)).stacks) tiers[st.slot] = Math.max(tiers[st.slot], st.tier + 1);
  return tiers;
}

/**
 * 69.14 (the owner: "making containers real / everything real and collidable in motor pool"): each pool's `stacks` synced to
 * what its side's stock stands in it — the look's own fill (rendering/motorPoolPlay.ts), so the layout's stack modules
 * (core/modules.ts motorPoolLayout: the walk's boxes, the rounds' shapes) are the frames drawn. The state as it was when no
 * pool's stacks changed (the Match runs it every tick after the stock's step: a compare, no copy, on a quiet tick).
 */
export function syncPoolStacks(state: GameState, tick = state.tick ?? 0): GameState {
  if (!state.motorPools) return state;
  let next: StructureState[] | null = null;
  state.structures.forEach((s, k) => {
    if (s.type !== 'motorPool' || s.health <= 0) return;
    const team = structureTeam(state, s);
    if (team === null) return;
    const tiers = stacksOf(state, team, tick);
    if (s.stacks && s.stacks.length === tiers.length && s.stacks.every((v, i) => v === tiers[i])) return;
    next ??= state.structures.slice();
    next[k] = { ...s, stacks: tiers };
  });
  return next ? { ...state, structures: next } : state;
}

/** The unpack pad: where an unpacked vehicle stands (the pad's middle, MOTOR_POOL.pad: one cell across, three along), facing out of the gate. */
export function poolPad(state: Pick<GameState, 'motorPools' | 'planets' | 'config'>, team: number): { planetIndex: number; dir: Vec3; heading: Vec3 } | null {
  const pool = poolOf(state, team), planet = pool ? state.planets[pool.planetIndex] : undefined;
  if (!pool || !planet) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  // the plan's frame on the ground: z toward the back (the gate at −z faces `front`), x across (front × up)
  const pad = MOTOR_POOL.pad, c = pad.alongX ? planCentre(MOTOR_POOL, pad.i, pad.j, 3, 1) : planCentre(MOTOR_POOL, pad.i, pad.j, 1, 3);
  const up = vecNormalize(pool.dir), front = vecNormalize(pool.front), across = vecNormalize(vecCross(front, up));
  const off = vecAdd(vecScale(across, c.x * person), vecScale(front, -c.z * person));
  // (69.14: along x, it faces the passage, +x — the gate a short turn on; along z, out of the gate's side, `front`)
  return { planetIndex: pool.planetIndex, dir: vecNormalize(vecAdd(up, vecScale(off, 1 / planet.radius))), heading: pad.alongX ? across : front };
}

/** A frame of `kind` off the stack to the pad: the stock spent by one, the unpack under way (its vehicle `ownerId`'s, its home `home`); or the refusal. */
export function startUnpack(state: GameState, team: number, kind: VehicleKind, tick: number, ownerId: number, home?: { surfaceDir: Vec3; heading: Vec3 }, armament?: StructureState['armament']): { state: GameState } | { refused: string } {
  const why = unpackRefusal(state, team, kind);
  if (why) return { refused: why };
  const pool = poolOf(state, team)!, stock = state.vehicleStocks![team]![kind]!, pad = poolPad(state, team)!;
  return {
    state: {
      ...state,
      vehicleStocks: { ...state.vehicleStocks, [team]: { ...state.vehicleStocks![team], [kind]: { ...stock, left: stock.left - 1 } } },
      motorPools: { ...state.motorPools, [team]: { ...pool, unpacking: { kind, doneTick: tick + Math.round(unpackSecondsOf(kind) / PHYS_DT), ownerId, home: home ?? { surfaceDir: pad.dir, heading: pad.heading }, ...(armament ? { armament } : {}) } } },   // (69.21: its variant carried)
    },
  };
}

/** The side's standing order: auto (absent) or by hand. */
export function setPoolOrder(state: GameState, team: number, mode: 'auto' | 'order'): GameState {
  const pool = poolOf(state, team);
  if (!pool || (mode === 'order') === !!pool.order) return state;
  const { order: _was, ...rest } = pool;
  return { ...state, motorPools: { ...state.motorPools, [team]: mode === 'order' ? { ...rest, order: true } : rest } };
}

/**
 * An unpack done: its vehicle stands on the side's ready line outside the gate (69.19: the corner robots roll it off the pad, so
 * the next frame starts at once; it stood on the pad, and the pad waited for a crew to drive it off) — on the first spot free
 * (readySpot); none free, it waits. With the event.
 */
export function finishUnpacks(state: GameState, tick: number): { state: GameState; done: Array<{ structureId: number; type: VehicleKind; team: number }> } {
  let next = state;
  const done: Array<{ structureId: number; type: VehicleKind; team: number }> = [];
  for (const team of Object.keys(state.motorPools ?? {}).map(Number).sort((a, b) => a - b)) {
    const pool = next.motorPools![team], u = pool.unpacking;
    if (!u || u.doneTick > tick) continue;
    const spot = readySpot(next, team, u.kind);
    if (!spot) continue;   // (the line full: it waits)
    const id = next.structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
    const vehicle: StructureState = {
      id, type: u.kind, planetIndex: spot.planetIndex, surfaceDir: spot.dir, health: structureHealth(u.kind), ownerId: u.ownerId,
      seats: initialSeat(u.kind, u.armament, next.config.tankStowage), drive: { heading: spot.heading, speed: 0, variant: 'gas' }, home: u.home, ...(u.armament ? { armament: u.armament } : {}), ...(u.armament === 'mortar' ? { mortar: { rounds: next.config.mortarStore ?? MORTAR.store } } : {}),   // (69.23)
    };
    const { unpacking: _u, ...rest } = pool;
    next = { ...next, structures: [...next.structures, vehicle], motorPools: { ...next.motorPools, [team]: rest } };
    done.push({ structureId: id, type: u.kind, team });
  }
  return { state: next, done };
}

/** 69.19: a kind's unpack, seconds (MOTOR_POOL_STOCK.unpackByKind; else the one time). */
export function unpackSecondsOf(kind: VehicleKind): number {
  return MOTOR_POOL_STOCK.unpackByKind[kind as keyof typeof MOTOR_POOL_STOCK.unpackByKind] ?? MOTOR_POOL_STOCK.unpackSeconds;
}

/**
 * 69.19: where a side's next unpacked vehicle of `kind` stands — the first free spot of its pool's ready line (READY_LINE: rows
 * outside the front gate, the nearest the gate first), facing out; a spot is free when no standing structure (the compound
 * aside) is within both footprints of it. Null: no pool, or every spot taken.
 */
export function readySpot(state: Pick<GameState, 'motorPools' | 'planets' | 'structures' | 'config'>, team: number, kind: VehicleKind): { planetIndex: number; dir: Vec3; heading: Vec3 } | null {
  const pool = state.motorPools?.[team], planet = pool ? state.planets[pool.planetIndex] : undefined;
  if (!pool || !planet) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const up = vecNormalize(pool.dir), front = vecNormalize(pool.front), across = vecNormalize(vecCross(front, up));
  const mine = structureFootprint({ type: kind }, state.config, planet.radius);
  for (const z of READY_LINE.rowsZ) {
    for (const x of READY_LINE.xs) {
      // (the plan's frame on the ground, as poolPad: x across, z toward the back — the gate at −z faces `front`)
      const off = vecAdd(vecScale(across, x * person), vecScale(front, -z * person));
      const dir = vecNormalize(vecAdd(up, vecScale(off, 1 / planet.radius)));
      if (state.structures.some(o => o.type !== 'motorPool' && o.planetIndex === pool.planetIndex && o.health > 0 && arcDistance(o.surfaceDir, dir, planet.radius) < mine + structureFootprint(o, state.config, planet.radius))) continue;
      return { planetIndex: pool.planetIndex, dir, heading: front };
    }
  }
  return null;
}

/** 69.19: a side's unwrapped share at the start (GameConfig.startUnwrapped: a share, or 'auto' by the match's roles). */
export function startUnwrappedShare(state: Pick<GameState, 'config' | 'modeState'>, team: number): number {
  const want = state.config.startUnwrapped;
  const v = want ? want[team] ?? want[0] : undefined;
  if (v === undefined) return 1;
  if (v !== 'auto') return Math.max(0, Math.min(1, v));
  const push = state.modeState.points?.push;
  if (push && team === push.defender) return START_UNWRAPPED.autoDefender;
  if (push && team === push.attacker) return START_UNWRAPPED.autoAttacker;
  return START_UNWRAPPED.autoNoRoles;
}

const isStockVehicle = (s: Pick<StructureState, 'type'>): s is StructureState & { type: VehicleKind } => (VEHICLE_KINDS as readonly string[]).includes(s.type);

/**
 * 69.19 (DECISIONS 237 (8), the owner: "no magic starting ground vehicles (except for dropship probably)"; "we need to be able
 * to control what's in reserve and whats unpacked for whom at the start of the match"; "A is fine"): each side with a pool keeps
 * its start fleet's unwrapped share standing where the setup parked it (startUnwrappedShare, rounded up a kind, so a share above
 * none keeps one of each); the rest are packed — taken off the field into the pool's stock (`left` up by one each; each kind's
 * cap the fleet it started with) and queued to unpack at once, in START_UNPACK_ORDER (core/vehicleStock.ts' queue: one at a
 * time on the pad, each to the ready line). Every vehicle of a side with a stock keeps a `home` (its parked spot), so a loss
 * comes back from the pool. The state as it was where nothing is packed.
 */
export function packStartFleet(state: GameState): GameState {
  if (!state.motorPools || !state.config.startUnwrapped) return state;
  const packed = new Set<number>();
  const stocks: NonNullable<GameState['vehicleStocks']> = { ...(state.vehicleStocks ?? {}) };
  const refits: NonNullable<GameState['vehicleRefits']> = [...(state.vehicleRefits ?? [])];
  const stocked = new Set<number>();
  const homeOf = (v: StructureState) => v.home ?? { surfaceDir: { ...v.surfaceDir }, heading: { ...(v.drive?.heading ?? surfaceTangentFrame(v.surfaceDir).tx) } };
  for (const team of Object.keys(state.motorPools).map(Number).sort((a, b) => a - b)) {
    const share = startUnwrappedShare(state, team);
    if (share >= 1) continue;
    const mine = state.structures.filter(v => isStockVehicle(v) && v.health > 0 && structureTeam(state, v) === team).sort((a, b) => a.id - b.id);
    for (const kind of START_UNPACK_ORDER) {
      const fleet = mine.filter(v => v.type === kind);
      if (!fleet.length) continue;
      const ready = share <= 0 ? 0 : Math.ceil(share * fleet.length - 1e-9);
      const pack = fleet.slice(ready);
      if (!pack.length) continue;
      const side = { ...(stocks[team] ?? {}) }, was = side[kind];
      side[kind] = { left: (was?.left ?? 0) + pack.length, cap: was?.cap ?? fleet.length };
      stocks[team] = side;
      stocked.add(team);
      for (const v of pack) {
        packed.add(v.id);
        refits.push({ team, type: kind, due: 0, planetIndex: v.planetIndex, home: homeOf(v), ownerId: v.ownerId, ...(v.armament ? { armament: v.armament } : {}) });   // (69.21: a packed variant unpacks as itself)
      }
    }
  }
  if (!packed.size) return state;
  // the fleet each packing side fields: capped at what it started with; the unwrapped ones homed (a loss replaced from the pool)
  for (const team of stocked) {
    const side = { ...stocks[team] }, fielded: Partial<Record<VehicleKind, number>> = {};
    for (const v of state.structures) if (isStockVehicle(v) && v.health > 0 && structureTeam(state, v) === team) fielded[v.type] = (fielded[v.type] ?? 0) + 1;
    for (const kind of Object.keys(fielded) as VehicleKind[]) if (!side[kind]) side[kind] = { left: 0, cap: fielded[kind]! };   // (a kind all unwrapped: no frame, its cap its fleet)
    stocks[team] = side;
  }
  const structures = state.structures.filter(v => !packed.has(v.id)).map(v => (isStockVehicle(v) && v.health > 0 && !v.home && stocked.has(structureTeam(state, v) ?? -1) ? { ...v, home: homeOf(v) } : v));
  return { ...state, structures, vehicleStocks: stocks, vehicleRefits: refits };
}

/**
 * 69.19 step 2: when each frame of `kinds` the side's pool has coming will stand on its ready line, seconds from `tick`, the
 * soonest first — the unpack on its pad (its time left), then its queue in order (63.1n's refits: each waits for its due tick
 * and the pad, then takes its kind's unpack). The side's `order` mode (the commander unpacks by hand) has none coming.
 */
export function poolArrivals(state: Pick<GameState, 'motorPools' | 'vehicleRefits'>, team: number, tick: number, kinds: readonly VehicleKind[]): number[] {
  const pool = state.motorPools?.[team];
  if (!pool || pool.order) return [];
  const out: number[] = [];
  let t = 0;
  if (pool.unpacking) {
    t = Math.max(0, (pool.unpacking.doneTick - tick) * PHYS_DT);
    if (kinds.includes(pool.unpacking.kind)) out.push(t);
  }
  for (const r of state.vehicleRefits ?? []) {
    if (r.team !== team) continue;
    t = Math.max(t, (r.due - tick) * PHYS_DT) + unpackSecondsOf(r.type);
    if (kinds.includes(r.type)) out.push(t);
  }
  return out;
}
